import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor(private firestore: Firestore) {
    // Check if user is already authenticated in session storage
    const isAuth = sessionStorage.getItem('dashboard_authenticated') === 'true';
    this.isAuthenticatedSubject.next(isAuth);
  }

  async authenticateWithPassword(password: string): Promise<boolean> {
    try {
      // Hash the password using a simple hash function
      const hashedPassword = await this.hashPassword(password);
      
      // Check against Firestore
      const authDoc = doc(this.firestore, 'auth', 'dashboard');
      const docSnap = await getDoc(authDoc);
      
      if (docSnap.exists()) {
        const storedHash = docSnap.data()['passwordHash'];
        
        if (hashedPassword === storedHash) {
          // Authentication successful
          this.isAuthenticatedSubject.next(true);
          sessionStorage.setItem('dashboard_authenticated', 'true');
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Authentication error:', error);
      return false;
    }
  }

  logout(): void {
    this.isAuthenticatedSubject.next(false);
    sessionStorage.removeItem('dashboard_authenticated');
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  private async hashPassword(password: string): Promise<string> {
    // Using Web Crypto API for hashing
    const encoder = new TextEncoder();
    const data = encoder.encode(password);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  // Method to store initial password hash (for admin setup)
  async setPasswordHash(password: string): Promise<void> {
    try {
      const hashedPassword = await this.hashPassword(password);
      // This would typically be done through admin interface
      // For now, we'll just log the hash that needs to be stored
      console.log('Password hash to store in Firestore:', hashedPassword);
      
      // You would uncomment this to actually store it:
      // const authDoc = doc(this.firestore, 'auth', 'dashboard');
      // await setDoc(authDoc, { passwordHash: hashedPassword });
    } catch (error) {
      console.error('Error setting password hash:', error);
      throw error;
    }
  }
}
